import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Plus, Trash2, Eye, Search, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const levelColors = {
  bronze: "bg-orange-50 text-orange-700",
  silver: "bg-gray-100 text-gray-700",
  gold: "bg-amber-50 text-amber-700",
  platinum: "bg-purple-50 text-purple-700",
};

export const DonorsPanel = ({ getAuthHeader }) => {
  const [donors, setDonors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [commForm, setCommForm] = useState({ comm_type: "email", notes: "" });
  const [form, setForm] = useState({
    name: "", email: "", phone: "", organization: "", donor_type: "individual", notes: "",
  });

  const fetchDonors = async () => {
    try {
      const [donorsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/donors`, getAuthHeader()),
        axios.get(`${API}/admin/donors/stats/summary`, getAuthHeader()),
      ]);
      setDonors(donorsRes.data);
      setStats(statsRes.data);
      setLoaded(true);
    } catch { toast.error("Failed to load donors"); }
  };

  if (!loaded) fetchDonors();

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/donors`, form, getAuthHeader());
      toast.success("Donor added");
      setShowForm(false);
      setForm({ name: "", email: "", phone: "", organization: "", donor_type: "individual", notes: "" });
      fetchDonors();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this donor?")) return;
    try {
      await axios.delete(`${API}/admin/donors/${id}`, getAuthHeader());
      toast.success("Deleted");
      fetchDonors();
      if (detail?.id === id) setDetail(null);
    } catch { toast.error("Failed"); }
  };

  const viewDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/donors/${id}`, getAuthHeader());
      setDetail(res.data);
    } catch { toast.error("Failed to load donor details"); }
  };

  const handleAddComm = async () => {
    if (!commForm.notes.trim() || !detail) return;
    try {
      await axios.post(`${API}/admin/donors/${detail.id}/communication`, commForm, getAuthHeader());
      toast.success("Communication logged");
      setCommForm({ comm_type: "email", notes: "" });
      viewDetail(detail.id);
    } catch { toast.error("Failed"); }
  };

  const filtered = donors.filter(d =>
    `${d.name} ${d.email} ${d.organization}`.toLowerCase().includes(search.toLowerCase())
  );

  const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

  if (detail) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => setDetail(null)}>Back to List</Button>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-900">{detail.name}</h3>
            <div className="space-y-1 text-sm">
              {detail.email && <p>Email: {detail.email}</p>}
              {detail.phone && <p>Phone: {detail.phone}</p>}
              {detail.organization && <p>Organization: {detail.organization}</p>}
              <p>Type: <span className="capitalize">{detail.donor_type}</span></p>
              <p>Level: <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${levelColors[detail.giving_level]}`}>{detail.giving_level}</span></p>
              <p className="font-medium">Total Donated: {fmt(detail.total_donated)}</p>
            </div>
            {detail.donation_history?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mt-4 mb-2">Donation History</h4>
                {detail.donation_history.map((d, i) => (
                  <div key={d.id || `donation-${i}`} className="bg-gray-50 p-2 rounded-lg mb-1 text-xs">
                    {fmt(d.amount)} - {d.payment_status} ({new Date(d.created_at).toLocaleDateString()})
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1"><MessageSquare className="w-4 h-4" /> Communications</h4>
            <div className="space-y-2 max-h-64 overflow-y-auto mb-3">
              {(detail.communication_history || []).map((c, i) => (
                <div key={c.id || i} className="bg-gray-50 p-3 rounded-lg text-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-warm-50 text-warm-700 font-medium capitalize">{c.type}</span>
                    <span className="text-xs text-gray-400">{new Date(c.date).toLocaleDateString()}</span>
                  </div>
                  <p className="text-gray-700">{c.notes}</p>
                </div>
              ))}
              {(!detail.communication_history || detail.communication_history.length === 0) && <p className="text-xs text-gray-400">No communications logged</p>}
            </div>
            <div className="space-y-2">
              <select value={commForm.comm_type} onChange={e => setCommForm({ ...commForm, comm_type: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="email">Email</option><option value="phone">Phone Call</option><option value="meeting">Meeting</option><option value="letter">Letter</option><option value="other">Other</option>
              </select>
              <textarea value={commForm.notes} onChange={e => setCommForm({ ...commForm, notes: e.target.value })} placeholder="Log communication details..." rows={3} className="w-full border rounded-lg px-3 py-2 text-sm" />
              <Button size="sm" onClick={handleAddComm} className="bg-warm-700 hover:bg-warm-800 text-white">Log Communication</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-warm-50 rounded-xl p-4">
            <p className="text-xs text-warm-600 font-medium">Total Donors</p>
            <p className="text-xl font-bold text-warm-700">{stats.total_donors}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4">
            <p className="text-xs text-emerald-600 font-medium">Total Donated</p>
            <p className="text-xl font-bold text-emerald-700">{fmt(stats.total_donated)}</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 col-span-2 md:col-span-1">
            <p className="text-xs text-purple-600 font-medium">By Type</p>
            <div className="flex gap-3 text-xs mt-1">
              {Object.entries(stats.by_type || {}).map(([k, v]) => (
                <span key={k} className="capitalize">{k}: {v}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search donors..." className="pl-10" />
        </div>
        <Button data-testid="add-donor-btn" onClick={() => setShowForm(!showForm)} className="bg-warm-700 hover:bg-warm-800 text-white gap-2">
          <Plus className="w-4 h-4" />{showForm ? "Cancel" : "Add Donor"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-gray-50 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="mt-1" /></div>
          <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Organization</Label><Input value={form.organization} onChange={e => setForm({ ...form, organization: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Type</Label>
            <select value={form.donor_type} onChange={e => setForm({ ...form, donor_type: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
              <option value="individual">Individual</option><option value="corporate">Corporate</option><option value="foundation">Foundation</option>
            </select>
          </div>
          <div><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
          <div><Button type="submit" className="bg-warm-700 hover:bg-warm-800 text-white">Add Donor</Button></div>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-100">
            <th className="text-left py-3 px-4 text-gray-500 font-medium">Name</th>
            <th className="text-left py-3 px-4 text-gray-500 font-medium hidden md:table-cell">Organization</th>
            <th className="text-left py-3 px-4 text-gray-500 font-medium hidden sm:table-cell">Type</th>
            <th className="text-left py-3 px-4 text-gray-500 font-medium">Level</th>
            <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map(d => (
              <tr key={d.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="py-3 px-4">
                  <p className="font-medium text-gray-900">{d.name}</p>
                  <p className="text-xs text-gray-400">{d.email}</p>
                </td>
                <td className="py-3 px-4 text-gray-600 hidden md:table-cell">{d.organization || "-"}</td>
                <td className="py-3 px-4 text-gray-600 capitalize hidden sm:table-cell">{d.donor_type}</td>
                <td className="py-3 px-4">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${levelColors[d.giving_level]}`}>{d.giving_level}</span>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => viewDetail(d.id)} className="h-8 w-8 p-0"><Eye className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(d.id)} className="h-8 w-8 p-0 text-red-500"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-gray-400">No donors found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
